import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users, UserPlus, UserX, Briefcase, CreditCard, SplitSquareHorizontal,
  ArrowUpCircle, Receipt, BarChart3, FilePlus, FileText, AlertTriangle,
  Ban, CheckCircle, StickyNote, DollarSign, CalendarCheck, CalendarClock,
  TrendingUp, TrendingDown, Award, Building2
} from "lucide-react";

interface ReportLink {
  title: string;
  url: string;
  icon: React.ElementType;
}

interface ReportSection {
  heading: string;
  icon: React.ElementType;
  links: ReportLink[];
}

const reportSections: ReportSection[] = [
  {
    heading: "Customers and Leads",
    icon: Users,
    links: [
      { title: "Leads", url: "/reports/leads", icon: UserPlus },
      { title: "New Customers", url: "/reports/new-customers", icon: UserPlus },
      { title: "Customers", url: "/reports/customers", icon: Users },
      { title: "Dormant Customers", url: "/reports/dormant-customers", icon: UserX },
      { title: "BDO Portfolio", url: "/reports/bdo-portfolio", icon: Briefcase },
    ],
  },
  {
    heading: "Payments and Expenses",
    icon: CreditCard,
    links: [
      { title: "Payments", url: "/reports/payment-breakdown", icon: CreditCard },
      { title: "Branch Partial Payments", url: "/reports/branch-partial-payments", icon: SplitSquareHorizontal },
      { title: "Over Payments", url: "/reports/over-payments", icon: ArrowUpCircle },
      { title: "Expenses", url: "/actions/expenses", icon: Receipt },
      { title: "Performance Report", url: "/reports/performance", icon: BarChart3 },
    ],
  },
  {
    heading: "Loans",
    icon: FileText,
    links: [
      { title: "New Loans", url: "/reports/new-loans", icon: FilePlus },
      { title: "Loans", url: "/actions/loans", icon: FileText },
      { title: "Overdue Installments", url: "/reports/overdue-installments", icon: AlertTriangle },
      { title: "Loan Defaulters", url: "/reports/loan-defaulters", icon: Ban },
      { title: "Closed Loans", url: "/reports/closed-loans", icon: CheckCircle },
    ],
  },
  {
    heading: "Performance Analysis",
    icon: TrendingUp,
    links: [
      { title: "Best Performing Branch", url: "/reports/best-performing-branch", icon: Building2 },
      { title: "Worst Performing Branch", url: "/reports/worst-performing-branch", icon: TrendingDown },
      { title: "Best Performing BDO", url: "/reports/best-performing-bdo", icon: Award },
      { title: "Worst Performing BDO", url: "/reports/worst-performing-bdo", icon: TrendingDown },
    ],
  },
  {
    heading: "Other Reports",
    icon: StickyNote,
    links: [
      { title: "Customer Notes", url: "/reports/customer-notes", icon: StickyNote },
      { title: "Loans Revenue", url: "/reports/loans-revenue", icon: DollarSign },
      { title: "Collection Calendar", url: "/financial/collections", icon: CalendarCheck },
      { title: "Disbursement Calendar", url: "/reports/disbursement-calendar", icon: CalendarClock },
    ],
  },
];

export default function ReportsPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Reports</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {reportSections.map((section) => {
          const SectionIcon = section.icon;
          return (
            <Card key={section.heading} className="overflow-hidden">
              <CardHeader className="bg-muted/50 pb-3 pt-4 px-5">
                <CardTitle className="flex items-center gap-2 text-base">
                  <SectionIcon className="h-5 w-5 text-primary" />
                  {section.heading}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {section.links.map((link) => {
                    const LinkIcon = link.icon;
                    return (
                      <button
                        key={link.title}
                        onClick={() => navigate(link.url)}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm font-medium text-foreground hover:bg-accent/50 transition-colors text-start"
                      >
                        <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                        {link.title}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
